import { useEffect, useState } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { getVisitPreSaleFeedbackEndpoint } from '../services/dashboardService';

export interface PreSaleVisitFeedbackData {
  id: string;
  visit_id: string;
  project_model_id: string;
  purchase_intent: string;
  objections: string[];
  compared_with: string | null;
  prospect_stage: string;
  created_at: string;
}

export type FeedbackState = 'loading' | 'done' | 'pending' | 'error';

// Feedback del Proper de una asesoría de PreVenta. 404 con código 1107 = aún
// no tiene feedback; cualquier otro error (1102, ruta inexistente) es 'error'.
const FEEDBACK_NOT_FOUND_CODE = 1107;

const usePreSaleVisitFeedback = (
  visitId?: string,
  enabled = true,
  asAdmin = false,
) => {
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [state, setState] = useState<FeedbackState>('loading');
  const [feedback, setFeedback] = useState<PreSaleVisitFeedbackData | null>(
    null,
  );
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!visitId || !enabled) return undefined;
    let active = true;
    setState('loading');
    (async () => {
      const { status, data } = await callEndpoint(
        getVisitPreSaleFeedbackEndpoint(visitId, token, asAdmin),
      );
      if (!active) return;
      if (status === 200) {
        const body = fetchResponseAdapter(data).data;
        setFeedback(body);
        setState('done');
      } else if (
        status === 404 &&
        fetchResponseAdapter(data).code === FEEDBACK_NOT_FOUND_CODE
      ) {
        setFeedback(null);
        setState('pending');
      } else {
        setState('error');
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId, enabled, reloadKey]);

  return { state, feedback, reload: () => setReloadKey((key) => key + 1) };
};

export default usePreSaleVisitFeedback;
